// InboxItemCard (M1 §8): um item da lista agregada — tipo, assunto/typeCode traduzido, prazo, badges.
import { Badge } from '@giyus/ui';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useDateFormatter } from '../../../lib/dates/useDateFormatter';
import type { WorkflowInboxItem } from '../api/contract.preview';

export interface InboxItemCardProps {
  readonly item: WorkflowInboxItem;
}

export function InboxItemCard({ item }: InboxItemCardProps) {
  const { t } = useTranslation('workflow');
  const dates = useDateFormatter();
  const typeCodeLabel =
    item.kind === 'task'
      ? t(`taskTypes.${item.typeCode}.name`, { defaultValue: item.typeCode })
      : item.kind === 'exception'
        ? t(`exceptionTypes.${item.typeCode}.name`, { defaultValue: item.typeCode })
        : item.typeCode;

  return (
    <li>
      <Link
        to={`/me/inbox/${item.kind}/${item.id}`}
        className="flex flex-col gap-1 rounded-card border p-3 text-body hover:bg-accent hover:text-accent-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
        data-testid={`inbox-item-${item.id}`}
      >
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline">{t(`kinds.${item.kind}`)}</Badge>
          <span className="font-medium">{item.subject ?? typeCodeLabel}</span>
          {item.overdue && (
            <Badge variant="destructive" data-testid="inbox-item-overdue">
              {t('inbox.item.overdue')}
            </Badge>
          )}
          {item.assignedToMe && <Badge variant="secondary">{t('inbox.item.assignedToMe')}</Badge>}
          {item.claimable && !item.assignedToMe && (
            <Badge variant="outline">{t('inbox.item.claimable')}</Badge>
          )}
        </div>
        <p className="text-caption text-muted-foreground">{typeCodeLabel}</p>
        <p className="text-caption text-muted-foreground">
          {item.dueAt
            ? t('inbox.item.dueAt', { when: dates.dateTime(item.dueAt) })
            : t('inbox.item.noDueDate')}
        </p>
      </Link>
    </li>
  );
}
